import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Stack,
  TablePagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { createLogger } from '../../utils/debugUtils';
import SaveResearchTemplateDialog from '../dialogs/SaveResearchTemplateDialog';
import { authenticatedFetch } from '../../utils/apiUtils';
import supabase from '../../utils/supabaseClient';

const logger = createLogger('ResearchResults');

function ResearchResults({ jobId, results, onBackToLists }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('quality_ranking');
  const sortOrder = 'desc'; // Default to descending order
  const [summary, setSummary] = useState({
    totalWebsites: 0,
    kept: 0,
    excluded: 0
  });
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  console.log('ResearchResults component rendering with:', { jobId, results });

  // Calculate summary statistics when results change
  useEffect(() => {
    if (!results || !results.websites) return;

    const totalWebsites = results.websites.length;
    const kept = results.websites.filter(site => site.decision === 'KEEP').length;
    const excluded = results.websites.filter(site => site.decision === 'EXCLUDE' || site.decision === 'ERROR').length;

    setSummary({
      totalWebsites,
      kept,
      excluded
    });
  }, [results]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    setPage(0); // Reset to first page when changing tabs
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle sorting
  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(0);
  };

  // Export results to Excel using backend endpoint
  const handleExportResults = async () => {
    if (!jobId) {
      logger.error('Cannot export without a valid job ID');
      return;
    }

    try {
      logger.info(`Exporting results for job ${jobId}`);
      
      const exportUrl = `/api/research/jobs/${jobId}/export`;
      
      // Get the current session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Authentication required. Please sign in again.');
      }
      
      // Use raw fetch with manual auth header to avoid Content-Type interference
      const response = await fetch(exportUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Export failed with status: ${response.status}`);
      }
      
      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `research-results-${jobId}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Get the blob data
      const blob = await response.blob();
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      URL.revokeObjectURL(url);
      
      logger.info('Excel export completed successfully');
    } catch (error) {
      logger.error('Error exporting Excel:', error);
      alert(`Failed to export results: ${error.message}`);
    }
  };

  // Format URL for display
  const formatDisplayUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url;
    }
  };

  // Transform research results to organized data
  const transformResultsData = (results) => {
    const websites = results?.websites || [];
    return {
      keptData: websites.filter(w => w.decision === 'KEEP'),
      excludedData: websites.filter(w => w.decision === 'EXCLUDE' || w.decision === 'ERROR'),
      websiteDecisions: websites,
      stats: {
        totalRows: websites.length,
        keptRowCount: websites.filter(w => w.decision === 'KEEP').length,
        excludedRowCount: websites.filter(w => w.decision === 'EXCLUDE' || w.decision === 'ERROR').length
      }
    };
  };

  // Get quality ranking color and display
  const getQualityRanking = (ranking, bonusScore) => {
    if (!ranking) return { label: 'N/A', color: '#9e9e9e', bg: 'rgba(158, 158, 158, 0.1)' };
    
    const bonus = bonusScore ? ` (+${bonusScore})` : '';
    
    switch (ranking) {
      case 'EXCELLENT':
        return { label: `Excellent${bonus}`, color: '#4caf50', bg: 'rgba(76, 175, 80, 0.1)' };
      case 'GOOD':
        return { label: `Good${bonus}`, color: '#FFD700', bg: 'rgba(255, 215, 0, 0.1)' };
      case 'ACCEPTABLE':
        return { label: `Acceptable${bonus}`, color: '#ff9800', bg: 'rgba(255, 152, 0, 0.1)' };
      default:
        return { label: 'Unknown', color: '#9e9e9e', bg: 'rgba(158, 158, 158, 0.1)' };
    }
  };

  // Sort data
  const sortData = (data) => {
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      switch(sortBy) {
        case 'quality_ranking':
          // Sort by quality ranking (EXCELLENT > GOOD > ACCEPTABLE), then by bonus score
          const qualityOrder = { 'EXCELLENT': 3, 'GOOD': 2, 'ACCEPTABLE': 1 };
          const aQuality = a.quality_ranking || a.qualityRanking;
          const bQuality = b.quality_ranking || b.qualityRanking;
          aValue = (qualityOrder[aQuality] || 0) * 100 + (parseFloat(a.bonus_score || a.bonusScore) || 0);
          bValue = (qualityOrder[bQuality] || 0) * 100 + (parseFloat(b.bonus_score || b.bonusScore) || 0);
          break;
        case 'url':
          aValue = formatDisplayUrl(a.url).toLowerCase();
          bValue = formatDisplayUrl(b.url).toLowerCase();
          break;
        case 'decision':
          aValue = a.decision;
          bValue = b.decision;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filter data based on search query
  const getFilteredData = (data) => {
    if (!searchQuery.trim()) return data;
    
    const query = searchQuery.toLowerCase().trim();
    
    return data.filter(item => {
      const url = formatDisplayUrl(item.url).toLowerCase();
      const reasoning = item.reasoning?.toLowerCase() || '';
      return url.includes(query) || reasoning.includes(query);
    });
  };

  // Get data for current tab with sorting, filtering, and pagination
  const getCurrentTabData = () => {
    const transformedData = transformResultsData(results);
    
    let tabData;
    switch(tabIndex) {
      case 0:
        tabData = transformedData.keptData;
        break;
      case 1:
        tabData = transformedData.excludedData;
        break;
      case 2:
        tabData = transformedData.websiteDecisions;
        break;
      default:
        tabData = [];
    }
    
    // Apply filtering
    const filteredData = getFilteredData(tabData);
    
    // Apply sorting
    const sortedData = sortData(filteredData);
    
    return {
      totalCount: sortedData.length,
      paginatedData: sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    };
  };

  const { totalCount, paginatedData } = getCurrentTabData();

  return (
    <Box sx={{ width: '100%' }}>
      {results && results.websites ? (
        <>
          {/* Header with Back Button */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBackToLists}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  color: '#0095FF',
                  borderColor: '#0095FF',
                  backgroundColor: 'rgba(0, 149, 255, 0.1)'
                }
              }}
            >
              Back to Lists
            </Button>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Save as Template">
                <IconButton
                  onClick={() => setSaveDialogOpen(true)}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      color: '#9C27B0',
                      backgroundColor: 'rgba(156, 39, 176, 0.1)'
                    }
                  }}
                >
                  <BookmarkIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Research Results">
                <IconButton
                  onClick={handleExportResults}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      color: '#0095FF',
                      backgroundColor: 'rgba(0, 149, 255, 0.1)'
                    }
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Stats Cards */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 3,
            maxWidth: '800px',
            mx: 'auto'
          }}>
            <Card sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid #ff980030',
              borderRadius: 3
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Accounts
                </Typography>
                <Typography variant="h4" sx={{ color: '#ff9800' }}>
                  {summary.totalWebsites}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid #0095FF30',
              borderRadius: 3
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Qualified
                </Typography>
                <Typography variant="h4" sx={{ color: '#0095FF' }}>
                  {summary.kept}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid #FF149330',
              borderRadius: 3
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Disqualified
                </Typography>
                <Typography variant="h4" sx={{ color: '#FF1493' }}>
                  {summary.excluded}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Tabs, Search, and Sort Controls */}
          <Paper sx={{ 
            backgroundColor: 'background.paper', // Use theme's Paper background (dark blue)
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: 3,
            mb: 3
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#9C27B0',
                  },
                  '& .Mui-selected': {
                    color: 'text.primary',
                    fontWeight: 600,
                  }
                }}
              >
                <Tab label="Qualified" />
                <Tab label="Disqualified" />
                <Tab label="All Decisions" />
                <Tab label="Research Criteria" />
              </Tabs>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {/* Only show sort controls for data tabs, not criteria tab */}
                {tabIndex !== 3 && (
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Sort by</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={handleSortChange}
                      label="Sort by"
                    >
                      <MenuItem value="quality_ranking">Quality</MenuItem>
                      <MenuItem value="url">Website</MenuItem>
                      {tabIndex === 2 && <MenuItem value="decision">Decision</MenuItem>}
                    </Select>
                  </FormControl>
                )}

                {/* Only show search for data tabs, not criteria tab */}
                {tabIndex !== 3 && (
                  <TextField
                    size="small"
                    placeholder="Search accounts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: '250px' }}
                  />
                )}
              </Box>
            </Box>

            {/* Results Grid - Only show for data tabs (0, 1, 2), not criteria tab (3) */}
            {tabIndex !== 3 && (
              <Box sx={{ p: 1 }}>
                {/* Column Headers */}
              {paginatedData.length > 0 && (
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: tabIndex === 2 ? '2fr 1fr 1fr 2fr 0.5fr' : '2fr 1fr 2fr 0.5fr'
                  },
                  gap: 1.5,
                  p: 2,
                  mb: 1,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, display: { xs: 'none', md: 'block' } }}>
                    Website
                  </Typography>
                  {tabIndex === 2 && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
                      Decision
                    </Typography>
                  )}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
                    Relevance
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, display: { xs: 'none', md: 'block' } }}>
                    Reasoning
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
                    Actions
                  </Typography>
                </Box>
              )}

              {paginatedData.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  color: 'text.secondary'
                }}>
                  <Typography>
                    {searchQuery.trim() 
                      ? `No results found for "${searchQuery}"` 
                      : 'No data available for this tab'}
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={0.25}>
                  {paginatedData.map((item, index) => {
                    const qualityRanking = getQualityRanking(item.quality_ranking || item.qualityRanking, item.bonus_score);
                    
                    return (
                      <Paper
                        key={index}
                        sx={{
                          p: 0.75,
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                          border: '1px solid rgba(255, 255, 255, 0.05)',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                          }
                        }}
                      >
                        <Box sx={{ 
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            md: tabIndex === 2 ? '2fr 1fr 1fr 2fr 0.5fr' : '2fr 1fr 2fr 0.5fr'
                          },
                          gap: 1,
                          alignItems: 'center'
                        }}>
                          {/* Website URL */}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                              {formatDisplayUrl(item.url)}
                            </Typography>
                          </Box>

                          {/* Decision (only on All Decisions tab) */}
                          {tabIndex === 2 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Chip
                                label={item.decision === 'KEEP' ? 'Qualified' : 'Disqualified'}
                                size="small"
                                sx={{
                                  backgroundColor: item.decision === 'KEEP' 
                                    ? 'rgba(0, 149, 255, 0.1)' 
                                    : 'rgba(255, 20, 147, 0.1)',
                                  color: item.decision === 'KEEP' ? '#0095FF' : '#FF1493',
                                  fontWeight: 500,
                                  border: item.decision === 'KEEP' 
                                    ? '1px solid #0095FF30' 
                                    : '1px solid #FF149330',
                                  height: '20px',
                                  fontSize: '0.65rem'
                                }}
                              />
                            </Box>
                          )}

                          {/* Quality Ranking */}
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Chip
                              label={qualityRanking.label}
                              size="small"
                              sx={{
                                backgroundColor: qualityRanking.bg,
                                color: qualityRanking.color,
                                fontWeight: 500,
                                border: `1px solid ${qualityRanking.color}30`,
                                height: '20px',
                                fontSize: '0.65rem'
                              }}
                            />
                          </Box>

                          {/* Reasoning */}
                          <Box>
                            <Tooltip title={item.reasoning || ''} arrow placement="top">
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  cursor: 'help',
                                  fontSize: '0.75rem',
                                  lineHeight: 1.2
                                }}
                              >
                                {item.reasoning}
                              </Typography>
                            </Tooltip>
                          </Box>

                          {/* Actions */}
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip title="Open website">
                              <IconButton
                                size="small"
                                component="a"
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  '&:hover': {
                                    color: '#0095FF',
                                    backgroundColor: 'rgba(0, 149, 255, 0.1)'
                                  },
                                  padding: '2px'
                                }}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              )}

              {/* Pagination */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                p: 2,
                borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                '& .MuiTablePagination-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                },
                '& .MuiTablePagination-select': {
                  color: 'rgba(255, 255, 255, 0.9)'
                },
                '& .MuiIconButton-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.3)'
                  }
                }
              }}>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`} accounts`
                  }
                />
              </Box>
              </Box>
            )}

            {/* Research Criteria Tab */}
            {tabIndex === 3 && (
              <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {/* Research Instructions and Web Search Status - Side by side */}
                  <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)', flex: '1 1 70%', minWidth: 0 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: '#0095FF' }}>Research Instructions</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {results?.job?.instructions || 'No research instructions available'}
                      </Typography>
                    </Paper>

                    <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)', flex: '1 1 30%', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ mb: 2, color: '#0095FF' }}>Web Search</Typography>
                      <Chip 
                        label={results?.job?.enable_web_search ? 'Enabled' : 'Disabled'} 
                        sx={{ 
                          backgroundColor: 'rgba(0, 149, 255, 0.1)',
                          color: '#0095FF',
                          fontWeight: 600,
                          mb: 1
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {results?.job?.enable_web_search 
                          ? 'Enhanced analysis with web search data'
                          : 'Analysis based on website content only'}
                      </Typography>
                    </Paper>
                  </Box>

                  {/* Target Column Configuration */}
                  <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#0095FF' }}>Target Column</Typography>
                    <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.01)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.03)' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#FF1493' }}>
                        {results?.job?.column_name || 'Website'}
                      </Typography>
                    </Box>
                  </Paper>
                </Stack>
              </Box>
            )}
          </Paper>
        </>
      ) : (
        <Paper sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: 3
        }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Results Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Results will appear here once the research job is completed.
          </Typography>
        </Paper>
      )}

      {/* Save Template Dialog */}
      <SaveResearchTemplateDialog
        open={saveDialogOpen}
        onClose={(saved) => {
          setSaveDialogOpen(false);
          if (saved) {
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
          }
        }}
        jobConfig={{
          instructions: results?.job?.instructions || '',
          enableQualification: results?.job?.enable_qualification !== false,
          enableCustomFields: results?.job?.enable_custom_fields || false,
          customFields: results?.job?.custom_field_config || [],
          enableWebSearch: results?.job?.enable_web_search || false,
          webSearchInstructions: results?.job?.web_search_instructions || ''
        }}
      />

      {/* Success Snackbar */}
      {saveSuccess && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            p: 2,
            backgroundColor: '#4caf50',
            color: 'white',
            borderRadius: 2,
            zIndex: 1300
          }}
        >
          <Typography variant="body2">Template saved successfully!</Typography>
        </Paper>
      )}
    </Box>
  );
}

export default ResearchResults;